import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { ClipLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import {
  CalendarIcon,
  ClockIcon,
  CreditCardIcon,
  DocumentTextIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

import {
  getPromoterEvents,
  duplicateEvent,
  publishEvent,
} from '../../../redux/actions/events';
import { getBrandEvents } from '../../../redux/actions/bookings';

import { useAlert } from '../../../context/AlertContext';

import EventManagerModal from 'components/EventManagerModal';
import ShareEventModal from 'components/ShareEventModal';
import CreateEventButton from 'components/CreateEventButton';
import useEasypayGate from 'hooks/useEasypayGate';
import MyEventCard from 'components/MyEventCard';
import MyBookingCard from 'components/MyBookingCard';
import normalizeEventDate from 'utils/normalizeEventDate';
import { getEffectiveBookingStatus } from 'utils/bookingActionability';

/* ───────────────────────── date helpers (unchanged logic) ─────────────────── */

const getEventWindow = (dateObj) => {
  if (!dateObj || !dateObj.type) return { start: null, end: null };

  if (dateObj.type === 'fixed') {
    const start = moment(
      `${dateObj.startDate} ${dateObj.openHours || '00:00'}`,
      'YYYY-MM-DD HH:mm'
    );
    const end = moment(
      `${dateObj.endDate} ${dateObj.closeHours || '23:59'}`,
      'YYYY-MM-DD HH:mm'
    );
    return { start, end };
  }

  if (
    dateObj.type === 'segmented' &&
    Array.isArray(dateObj.days) &&
    dateObj.days.length
  ) {
    const sorted = [...dateObj.days]
      .filter((d) => d?.date)
      .sort((a, b) => moment(a.date).diff(moment(b.date)));
    const first = sorted[0];
    const last = sorted[sorted.length - 1];

    const start = moment(
      `${first.date} ${first.openHours || '00:00'}`,
      'YYYY-MM-DD HH:mm'
    );
    const end = moment(
      `${last.date} ${last.closeHours || '23:59'}`,
      'YYYY-MM-DD HH:mm'
    );
    return { start, end };
  }

  return { start: null, end: null };
};

const sortByStartAsc = (a, b) => {
  const { start: sa } = getEventWindow(a.date || {});
  const { start: sb } = getEventWindow(b.date || {});
  if (!sa && !sb) return 0;
  if (!sa) return 1;
  if (!sb) return -1;
  return sa.valueOf() - sb.valueOf();
};

const sortByEndDesc = (a, b) => {
  const { end: ea } = getEventWindow(a.date || {});
  const { end: eb } = getEventWindow(b.date || {});
  if (!ea && !eb) return 0;
  if (!ea) return 1;
  if (!eb) return -1;
  return eb.valueOf() - ea.valueOf();
};

/* ───────────────────────── booking status helpers ─────────────────────────── */

// Order brand bookings by how urgently they need the brand's attention
// (T2B-384): pay first, then awaiting review, then settled, with
// rejected/expired sinking to the bottom.
const BOOKING_GROUP_PRIORITY = {
  payNow: 0,
  awaiting: 1,
  confirmed: 2,
  rejected: 3,
  expired: 4,
};

const getBookingGroup = (booking) => {
  const status = getEffectiveBookingStatus(booking);
  if (status === 'expired') return 'expired';
  if (status === 'rejected' || status === 'cancelled' || status === 'canceled')
    return 'rejected';
  if (status === 'approved') return 'payNow';
  if (status === 'paid' || status === 'confirmed') return 'confirmed';
  return 'awaiting';
};

/* ───────────────────────── small UI helpers ───────────────────────────────── */

const Eyebrow = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.14em] text-muted font-bold mb-1.5">
    {children}
  </div>
);

const TabHeader = ({ eyebrow, title, sub, actions }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-6 mb-6 pb-5 border-b border-line">
    <div className="min-w-0">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="text-[26px] sm:text-[30px] md:text-[34px] leading-[1.05] tracking-tight text-ink-strong font-bold break-words">
        {title}
      </h2>
      {sub && (
        <p className="text-[14px] text-muted mt-2 max-w-xl leading-relaxed">
          {sub}
        </p>
      )}
    </div>
    {actions && (
      <div className="flex items-center gap-2 flex-wrap">{actions}</div>
    )}
  </div>
);

const Card = ({ children, className = '', padded = true }) => (
  <section
    className={`bg-white border border-line rounded-2xl shadow-softer ${
      padded ? 'p-6' : ''
    } ${className}`}
  >
    {children}
  </section>
);

const FilterChip = ({ active, onClick, label, count, icon: Icon }) => (
  <button
    type="button"
    onClick={onClick}
    className={`shrink-0 inline-flex items-center gap-2 h-9 px-4 rounded-full text-[12.5px] font-bold transition-colors ${
      active
        ? 'bg-ink-strong text-white'
        : 'bg-white text-muted border border-line hover:text-ink-strong'
    }`}
  >
    {Icon && <Icon className="w-4 h-4" />}
    {label}
    {typeof count === 'number' && (
      <span
        className={`font-mono text-[10.5px] ${active ? 'text-white/80' : 'text-muted'}`}
      >
        {count}
      </span>
    )}
  </button>
);

/* ───────────────────────────── MyEvents ──────────────────────────────────── */

const MyEvents = ({ user, deepLink, onClearDeepLink }) => {
  const isPromoter = user.role === 'promoter';
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const promoterEvents = useSelector((state) => state.events.promoterEvents);
  const brandEvents = useSelector((state) => state.bookings.brandEvents || []);
  const eventsLoading = useSelector((state) =>
    isPromoter ? state.events.loading : state.bookings.loading
  );

  const { showAlert } = useAlert();
  const { blocked: easypayBlocked } = useEasypayGate();

  const [isManagerModalOpen, setIsManagerModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [initialTabKey, setInitialTabKey] = useState(null);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [publishingId, setPublishingId] = useState(null);
  // The share pop-up: opened from a card's share button, or by itself right
  // after publishing a draft ({ event, justPublished }).
  const [shareTarget, setShareTarget] = useState(null);

  const [activeTab, setActiveTab] = useState('activeUpcoming');
  const [promoterBuckets, setPromoterBuckets] = useState({
    activeUpcoming: [],
    drafts: [],
    rejectedCancelled: [],
    past: [],
  });
  const [brandBuckets, setBrandBuckets] = useState({
    activeUpcoming: [],
    pendingPayment: [],
    expiredRejected: [],
    past: [],
  });

  useEffect(() => {
    if (isPromoter) {
      dispatch(
        getPromoterEvents({
          withSpaces: true,
          lang: i18n.language,
          pageSize: 50,
        })
      );
    } else {
      dispatch(getBrandEvents());
    }
  }, [dispatch, isPromoter, i18n.language]);

  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState !== 'visible') return;
      if (isPromoter) {
        dispatch(
          getPromoterEvents({
            withSpaces: true,
            lang: i18n.language,
            pageSize: 50,
          })
        );
      } else {
        dispatch(getBrandEvents());
      }
    };
    document.addEventListener('visibilitychange', onFocus);
    return () => document.removeEventListener('visibilitychange', onFocus);
  }, [dispatch, isPromoter, i18n.language]);

  useEffect(() => {
    if (!isPromoter) return;

    const now = moment();
    const src = promoterEvents || [];

    const dedupById = new Map();
    src.forEach((ev) => dedupById.set(ev.id, ev));
    const events = Array.from(dedupById.values());

    const active = [];
    const upcoming = [];
    const drafts = [];
    const rejectedCancelled = [];
    const past = [];

    for (const ev of events) {
      const status = String(ev.status || '').toLowerCase();
      // Before the date check: a duplicate is a draft with its dates cleared,
      // and it must still show up somewhere the promoter can find it.
      if (status === 'draft') {
        drafts.push(ev);
        continue;
      }

      const { start, end } = getEventWindow(normalizeEventDate(ev.date) || {});
      if (!start || !end) continue;

      if (
        status === 'rejected' ||
        status === 'cancelled' ||
        status === 'canceled'
      ) {
        rejectedCancelled.push(ev);
      } else if (now.isBefore(start)) upcoming.push(ev);
      else if (now.isSameOrAfter(end)) past.push(ev);
      else active.push(ev);
    }

    setPromoterBuckets({
      activeUpcoming: [
        ...active.sort(sortByStartAsc),
        ...upcoming.sort(sortByStartAsc),
      ],
      // Newest draft first — the one just saved or duplicated is on top.
      drafts: drafts.sort((a, b) => (b.id || 0) - (a.id || 0)),
      rejectedCancelled: rejectedCancelled.sort(sortByEndDesc),
      past: past.sort(sortByEndDesc),
    });
  }, [isPromoter, promoterEvents]);

  useEffect(() => {
    if (isPromoter) return;

    const now = moment();
    const rows = (brandEvents || [])
      .filter((ev) => ev?.booking)
      .map((ev) => ({ ...ev.booking, event: ev }));

    const buckets = {
      activeUpcoming: [],
      pendingPayment: [],
      expiredRejected: [],
      past: [],
    };

    for (const row of rows) {
      const { start, end } = getEventWindow(
        normalizeEventDate(row.event?.date) || {}
      );
      if (!start || !end) continue;

      const group = getBookingGroup(row);
      const ended = now.isSameOrAfter(end);

      if (group === 'rejected' || group === 'expired') {
        buckets.expiredRejected.push(row);
        continue;
      }
      if (ended) {
        // Past only keeps bookings the brand actually took part in.
        if (group === 'confirmed') buckets.past.push(row);
        continue;
      }
      buckets.activeUpcoming.push(row);
      if (group === 'payNow') buckets.pendingPayment.push(row);
    }

    const byPriorityThenStart = (a, b) => {
      const pa = BOOKING_GROUP_PRIORITY[getBookingGroup(a)];
      const pb = BOOKING_GROUP_PRIORITY[getBookingGroup(b)];
      if (pa !== pb) return pa - pb;
      return sortByStartAsc(a.event, b.event);
    };
    const byStartAsc = (a, b) => sortByStartAsc(a.event, b.event);
    const byEndDesc = (a, b) => sortByEndDesc(a.event, b.event);

    buckets.activeUpcoming.sort(byPriorityThenStart);
    buckets.pendingPayment.sort(byStartAsc);
    buckets.expiredRejected.sort(byEndDesc);
    buckets.past.sort(byEndDesc);

    setBrandBuckets(buckets);
  }, [isPromoter, brandEvents]);

  const handleOpenManage = (ev, tabKey = null) => {
    setSelectedEvent(ev);
    if (tabKey) setInitialTabKey(tabKey);
    setIsManagerModalOpen(true);
  };
  const handleCloseManage = () => {
    setSelectedEvent(null);
    setInitialTabKey(null);
    setIsManagerModalOpen(false);
  };

  const handleDuplicate = async (ev) => {
    if (isDuplicating) return;

    // A copy always starts as a draft with its dates cleared, so duplicating
    // is open without Easypay — publishing the copy is what needs it.
    setIsDuplicating(true);
    try {
      const newEvent = await dispatch(duplicateEvent(ev.id));
      if (newEvent?.id) {
        showAlert({
          title: t('dashboard.duplicate_success_title', 'Event duplicated'),
          message: t(
            'dashboard.duplicate_success_message',
            'Your event has been duplicated as a draft. Please update the dates before publishing.'
          ),
          primaryAction: {
            label: t('dashboard.duplicate_success_action', 'Edit now'),
            onClick: () => handleOpenManage(newEvent, 'edit'),
          },
        });
      }
    } catch {
      showAlert({
        title: t('dashboard.duplicate_error_title', 'Duplication failed'),
        message: t(
          'dashboard.duplicate_error_message',
          'Something went wrong while duplicating the event. Please try again.'
        ),
        primaryAction: { label: t('common.ok') },
      });
    } finally {
      setIsDuplicating(false);
    }
  };

  // Draft → live. The card only offers this to a connected promoter, but the
  // API decides; its refusals each get a message the promoter can act on.
  const handlePublish = async (ev) => {
    if (publishingId) return;
    setPublishingId(ev.id);
    try {
      const res = await dispatch(publishEvent(ev.id));
      setActiveTab('activeUpcoming');
      // Live now: offer what to share so brands start booking.
      setShareTarget({
        event: { ...ev, slug: res?.slug || ev.slug, status: res?.status || 'Pending' },
        justPublished: true,
      });
    } catch (err) {
      const code = err?.response?.data?.code;
      const needsDates =
        code === 'EVENT_DATE_REQUIRED' || code === 'EVENT_DATE_PASSED';
      showAlert({
        type: 'error',
        title: t('dashboard.publish_error_title'),
        message:
          code === 'EASYPAY_ACCOUNT_REQUIRED'
            ? t('event_create.publish_locked_error')
            : code === 'EVENT_DATE_REQUIRED'
              ? t('dashboard.publish_error_no_dates')
              : code === 'EVENT_DATE_PASSED'
                ? t('dashboard.publish_error_past_dates')
                : t('dashboard.publish_error_message'),
        primaryAction: needsDates
          ? {
              label: t('dashboard.publish_error_edit_dates'),
              onClick: () => handleOpenManage(ev, 'edit'),
            }
          : { label: t('common.ok') },
      });
    } finally {
      setPublishingId(null);
    }
  };

  useEffect(() => {
    if (!selectedEvent || isPromoter) return;
    const fresh = brandEvents.find((e) => e.id === selectedEvent.id);
    if (fresh && fresh !== selectedEvent) {
      setSelectedEvent(fresh);
    }
  }, [brandEvents, selectedEvent, isPromoter]);

  useEffect(() => {
    if (!deepLink) return;
    // `?list=drafts` — where the wizard sends a promoter after saving a draft.
    if (deepLink.list) {
      setActiveTab(deepLink.list);
      onClearDeepLink?.();
      return;
    }
    const targetId = String(deepLink.eventId || '');
    if (!targetId) return;

    if (isPromoter) {
      if (!Array.isArray(promoterEvents) || promoterEvents.length === 0) return;
      const ev = promoterEvents.find((e) => String(e.id) === targetId);
      if (ev) {
        handleOpenManage(ev, deepLink.emTab || null);
        onClearDeepLink?.();
      }
    } else {
      if (!Array.isArray(brandEvents) || brandEvents.length === 0) return;
      const ev = brandEvents.find((e) => String(e.id) === targetId);
      if (ev) {
        handleOpenManage(ev, deepLink.emTab || 'booking-status');
        onClearDeepLink?.();
      }
    }
  }, [deepLink, isPromoter, promoterEvents, brandEvents, onClearDeepLink]);

  const filters = useMemo(() => {
    if (isPromoter) {
      return [
        {
          key: 'activeUpcoming',
          label: t('dashboard.active_upcoming'),
          icon: CalendarIcon,
          list: promoterBuckets.activeUpcoming,
        },
        {
          key: 'drafts',
          label: t('dashboard.my_events_page.filter_drafts'),
          icon: DocumentTextIcon,
          list: promoterBuckets.drafts,
        },
        {
          key: 'rejectedCancelled',
          label: t(
            'dashboard.my_events_page.filter_rejected_cancelled',
            'Rejected & Cancelled'
          ),
          icon: XCircleIcon,
          list: promoterBuckets.rejectedCancelled,
        },
        {
          key: 'past',
          label: t('dashboard.past_events'),
          icon: ClockIcon,
          list: promoterBuckets.past,
        },
      ];
    }
    return [
      {
        key: 'activeUpcoming',
        label: t('dashboard.active_upcoming'),
        icon: CalendarIcon,
        list: brandBuckets.activeUpcoming,
      },
      {
        key: 'pendingPayment',
        label: t(
          'dashboard.my_events_page.filter_pending_payment',
          'Pending Payment'
        ),
        icon: CreditCardIcon,
        list: brandBuckets.pendingPayment,
      },
      {
        key: 'expiredRejected',
        label: t(
          'dashboard.my_events_page.filter_expired_rejected',
          'Expired & Rejected'
        ),
        icon: XCircleIcon,
        list: brandBuckets.expiredRejected,
      },
      {
        key: 'past',
        label: t('dashboard.past_events'),
        icon: ClockIcon,
        list: brandBuckets.past,
      },
    ];
  }, [isPromoter, t, promoterBuckets, brandBuckets]);

  const activeFilter = filters.find((f) => f.key === activeTab) || filters[0];
  const currentList = activeFilter.list;

  const renderEventGrid = (events) => (
    <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3">
      {events.map((ev) => (
        <MyEventCard
          key={ev.id}
          event={ev}
          onManageClick={() => handleOpenManage(ev)}
          onDuplicateClick={isPromoter ? handleDuplicate : undefined}
          onPublishClick={isPromoter ? handlePublish : undefined}
          onShareClick={
            isPromoter ? () => setShareTarget({ event: ev }) : undefined
          }
          publishLocked={easypayBlocked}
          publishing={publishingId === ev.id}
        />
      ))}
    </div>
  );

  const renderBookingGrid = (bookings) => (
    <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3">
      {bookings.map((b) => {
        const ev = b.event;
        const bookingEvent = { ...ev, booking: b };
        return (
          <MyBookingCard
            key={`${ev.id}-${b.id}`}
            event={bookingEvent}
            onManageClick={(targetEvent = bookingEvent, tabKey = null) =>
              handleOpenManage(targetEvent, tabKey)
            }
          />
        );
      })}
    </div>
  );

  const title = isPromoter
    ? t('dashboard.sidebar.my_events')
    : t('dashboard.sidebar.my_bookings');
  const subtitle = isPromoter
    ? t('dashboard.my_events_page.subtitle_promoter')
    : t('dashboard.my_events_page.subtitle_brand');
  const eyebrow = isPromoter
    ? t('dashboard.my_events_page.eyebrow_promoter')
    : t('dashboard.my_events_page.eyebrow_brand');

  const emptyCopyByFilter = {
    activeUpcoming: {
      title: isPromoter
        ? t('dashboard.my_events_page.empty_upcoming_title_promoter')
        : t('dashboard.my_events_page.empty_upcoming_title_brand'),
      sub: isPromoter
        ? t('dashboard.my_events_page.empty_upcoming_sub_promoter')
        : t('dashboard.my_events_page.empty_upcoming_sub_brand'),
    },
    pendingPayment: {
      title: t(
        'dashboard.my_events_page.empty_pending_payment_title',
        'No pending payments'
      ),
      sub: t(
        'dashboard.my_events_page.empty_pending_payment_sub',
        'Approved bookings waiting for payment will appear here.'
      ),
    },
    expiredRejected: {
      title: t(
        'dashboard.my_events_page.empty_expired_rejected_title',
        'No expired or rejected bookings'
      ),
      sub: t(
        'dashboard.my_events_page.empty_expired_rejected_sub',
        'Rejected bookings and expired payment windows will appear here.'
      ),
    },
    drafts: {
      title: t('dashboard.my_events_page.empty_drafts_title'),
      sub: t('dashboard.my_events_page.empty_drafts_sub'),
    },
    rejectedCancelled: {
      title: t(
        'dashboard.my_events_page.empty_rejected_cancelled_title',
        'No rejected or cancelled events'
      ),
      sub: t(
        'dashboard.my_events_page.empty_rejected_cancelled_sub',
        'Rejected and cancelled events will appear here.'
      ),
    },
    past: {
      title: isPromoter
        ? t('dashboard.my_events_page.empty_past_title_promoter')
        : t('dashboard.my_events_page.empty_past_title_brand'),
      sub: isPromoter
        ? t('dashboard.my_events_page.empty_past_sub_promoter')
        : t('dashboard.my_events_page.empty_past_sub_brand'),
    },
  };
  const emptyCopy =
    emptyCopyByFilter[activeFilter.key] || emptyCopyByFilter.activeUpcoming;
  const EmptyIcon = activeFilter.icon;

  return (
    <div className="space-y-6 relative">
      {eventsLoading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 backdrop-blur-[1px] rounded-2xl">
          <ClipLoader color="#FFBD59" size={32} />
        </div>
      )}

      <TabHeader
        eyebrow={eyebrow}
        title={title}
        sub={subtitle}
        actions={isPromoter ? <CreateEventButton /> : null}
      />

      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
        {filters.map((f) => (
          <FilterChip
            key={f.key}
            active={activeFilter.key === f.key}
            onClick={() => setActiveTab(f.key)}
            label={f.label}
            count={f.list.length}
            icon={f.icon}
          />
        ))}
      </div>

      {currentList.length > 0 ? (
        isPromoter ? (
          renderEventGrid(currentList)
        ) : (
          renderBookingGrid(currentList)
        )
      ) : (
        <Card>
          <div className="py-10 text-center">
            <div className="w-12 h-12 rounded-2xl bg-brand-softer text-yellow-03 mx-auto mb-3 flex items-center justify-center">
              <EmptyIcon className="w-6 h-6" />
            </div>
            <h3 className="text-[16px] font-bold text-ink-strong">
              {emptyCopy.title}
            </h3>
            <p className="text-[13px] text-muted mt-1 max-w-md mx-auto">
              {emptyCopy.sub}
            </p>
            {isPromoter && activeTab === 'activeUpcoming' && (
              <CreateEventButton className="mt-5" />
            )}
          </div>
        </Card>
      )}

      <ShareEventModal
        event={shareTarget?.event || null}
        justPublished={Boolean(shareTarget?.justPublished)}
        onClose={() => setShareTarget(null)}
      />

      {isManagerModalOpen && selectedEvent && (
        <EventManagerModal
          key={selectedEvent?.id || 'closed'}
          isOpen={isManagerModalOpen}
          onClose={handleCloseManage}
          event={selectedEvent}
          user={user}
          isAuthenticated
          initialTabKey={initialTabKey}
        />
      )}
    </div>
  );
};

export default MyEvents;
